import React from "react";
import { useSelector } from "react-redux";
import { hasPermission } from "../store/userSlice"; // Adjust the import path as necessary

/**
 * Renders children only if the user possesses the specified permission.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - The content to render conditionally.
 * @param {string[]} props.requiredPermission - An array representing the permission parts (e.g., ['vehicles', 'update']).
 * @param {React.ReactNode} [props.fallback=null] - Content to render if permission is denied.
 */
const PermissionRender = ({
  children,
  requiredPermission,
  fallback = null,
}) => {
  // Call useSelector unconditionally at the top level
  const canAccess = useSelector((state) => {
    // Perform the check inside the selector, but ensure the hook itself is always called
    if (!Array.isArray(requiredPermission) || requiredPermission.length === 0) {
      // If the permission array is invalid, treat as no access within the selector logic
      return false;
    }
    // The spread operator (...) passes the elements of requiredPermission as individual arguments
    return hasPermission(state, ...requiredPermission);
  });

  // Validate that requiredPermission is an array *after* the hook call for logging purposes
  if (!Array.isArray(requiredPermission) || requiredPermission.length === 0) {
    console.error(
      "PermissionRender: 'requiredPermission' prop must be a non-empty array (e.g., ['resource', 'action']). Received:",
      requiredPermission
    );
    // The rendering logic below will handle the fallback based on canAccess being false
  }

  // Render children if permission is granted, otherwise render the fallback
  if (!canAccess) {
    return fallback;
  }

  if (process.env.NODE_ENV === "development") {
    const permissionStr = requiredPermission.join(".");
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, {
          "data-permission": permissionStr,
          title: `Permission: ${permissionStr}`,
          style: { ...child.props.style, outline: "2px dotted #6e00ff" },
        });
      }
      return child;
    });
  }

  return <>{children}</>;
};

export default PermissionRender;
